// src/component/ui/PremiumCard.jsx

import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import LockIcon from '@mui/icons-material/Lock';
import { styled } from '@mui/material/styles';

const CardWrapper = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  cursor: 'pointer',
}));

const RibbonBadge = styled(Box)(({ theme, badgecolor }) => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  backgroundColor: badgecolor || theme.palette.secondary.main,
  color: '#FFFFFF',
  padding: '4px 10px',
  borderRadius: '8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
}));

const LockOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '12px',
  left: '12px',
  backgroundColor: 'rgba(15, 23, 42, 0.75)',
  color: '#FFFFFF',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
}));

/**
 * PremiumCard - Reusable premium card following 20px radius and hover standards
 * @param {Object} props
 * @param {string} props.title - Main card title
 * @param {string} [props.subtitle] - Supporting text or stats
 * @param {boolean} [props.locked=false] - If active, displays a top-left lock overlay
 * @param {number} [props.progress] - Completion percentage (0 - 100)
 * @param {string} [props.badgeText] - Optional top-right pill text
 * @param {string} [props.badgeColor] - Background color of the pill
 * @param {React.ReactNode} [props.children] - Additional content
 * @param {function} [props.onClick] - Click handler
 */
export const PremiumCard = ({
  title,
  subtitle,
  locked = false,
  progress,
  badgeText,
  badgeColor,
  children,
  onClick,
  ...rest
}) => {
  return (
    <CardWrapper onClick={onClick} {...rest}>
      {locked && (
        <LockOverlay>
          <LockIcon fontSize="small" />
        </LockOverlay>
      )}
      
      {badgeText && (
        <RibbonBadge badgecolor={badgeColor}>
          {badgeText}
        </RibbonBadge>
      )}

      <CardContent style={{ flexGrow: 1, padding: '24px' }}>
        <Typography variant="h3" component="h3" style={{ marginBottom: '8px', paddingRight: badgeText ? '60px' : '0' }}>
          {title}
        </Typography>
        
        {subtitle && (
          <Typography variant="body1" color="textSecondary" style={{ marginBottom: '16px' }}>
            {subtitle}
          </Typography>
        )}
        
        {children}

        {progress !== undefined && (
          <Box style={{ marginTop: 'auto', paddingTop: '16px' }}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <Typography variant="caption" color="textSecondary">Progress</Typography>
              <Typography variant="caption" color="textPrimary" style={{ fontWeight: 600 }}>{progress}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              color={progress === 100 ? "success" : "primary"}
              style={{ height: '6px', borderRadius: '3px' }}
            />
          </Box>
        )}
      </CardContent>
    </CardWrapper>
  );
};

export default PremiumCard;
